import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Building2, X } from "lucide-react";

const LS_KEY = "munja_dept_filter";

/**
 * DepartmentFilter — persistent department dropdown (URL + localStorage).
 *
 * Props:
 *  - value: current selected department_id (controlled from parent)
 *  - onChange: (id: string) => void
 *  - persistKey: optional localStorage key override (default: LS_KEY per module)
 *  - module: string — used as scope for localStorage so different pages have independent filters
 *  - testid: string
 *  - size: 'sm' | 'md' (default md)
 *  - showClear: bool (default true)
 */
export default function DepartmentFilter({
  value, onChange, module = "global", testid = "dept-filter",
  size = "md", showClear = true, className = "",
}) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/departments").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const selected = useMemo(() => items.find((x) => x.id === value), [items, value]);
  const padding = size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm";

  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`} data-testid={testid}>
      <Building2 className="w-3.5 h-3.5 text-zinc-500 shrink-0"/>
      <div className="relative inline-flex items-center">
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${padding} bg-zinc-950 border border-zinc-800 rounded text-zinc-100 pr-8 cursor-pointer appearance-none focus:border-blue-500 focus:outline-none`}
          style={{
            ...(selected ? { borderColor: `${selected.color}55`, backgroundColor: `${selected.color}10` } : {}),
          }}
          data-testid={`${testid}-select`}
        >
          <option value="">Alle Abteilungen</option>
          {items.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        {selected && (
          <span
            className="absolute left-2 pointer-events-none w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: selected.color, left: "7px" }}
          />
        )}
        {showClear && value && (
          <button
            onClick={() => onChange("")}
            title="Filter zurücksetzen"
            data-testid={`${testid}-clear`}
            className="absolute right-1.5 text-zinc-500 hover:text-red-400"
            style={{ pointerEvents: "auto" }}
          >
            <X className="w-3 h-3"/>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * useDepartmentFilter — hook binding a department filter to URL + localStorage.
 *
 * Usage:
 *   const [deptId, setDeptId] = useDepartmentFilter("mitarbeiter");
 *   // reads ?dept=... from URL (takes precedence), falls back to localStorage[LS_KEY:mitarbeiter]
 */
export function useDepartmentFilter(module) {
  const [params, setParams] = useSearchParams();
  const lsKey = `${LS_KEY}:${module}`;
  const urlVal = params.get("dept") || "";
  const initial = urlVal || (typeof window !== "undefined" ? window.localStorage.getItem(lsKey) || "" : "");
  const [value, setValue] = useState(initial);

  // Sync URL if localStorage existed and URL didn't
  useEffect(() => {
    if (!urlVal && initial) {
      const next = new URLSearchParams(params);
      next.set("dept", initial);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If URL changes externally, reflect
  useEffect(() => {
    if (urlVal !== value) setValue(urlVal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlVal]);

  function update(id) {
    setValue(id);
    if (typeof window !== "undefined") {
      if (id) window.localStorage.setItem(lsKey, id);
      else window.localStorage.removeItem(lsKey);
    }
    const next = new URLSearchParams(params);
    if (id) next.set("dept", id);
    else next.delete("dept");
    setParams(next, { replace: true });
  }

  return [value, update];
}
